import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap, catchError, of, Observable } from 'rxjs';
import { AppSettings } from '../config/app-settings';

export interface User {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'director';
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';
  
  public user = signal<User | null>(this.getStoredUser());
  public isAuthenticated = signal<boolean>(!!this.getToken());

  private getStoredUser(): User | null {
    const userJson = localStorage.getItem(this.USER_KEY);
    if (!userJson) return null;
    
    try {
      return JSON.parse(userJson);
    } catch (e) {
      console.error('Error parsing stored user', e);
      localStorage.removeItem(this.USER_KEY);
      return null;
    }
  }

  public getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  public login(credentials: { email: string; password: string }): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(AppSettings.AUTH_LOGIN, credentials).pipe(
      tap(response => {
        this.setSession(response);
      })
    );
  }

  public logout(): void {
    this.http.post(AppSettings.AUTH_LOGOUT, {}).subscribe();
    this.clearSession();
  }

  public refreshUser(): Observable<User | null> {
    const token = this.getToken();
    if (!token) {
      this.clearSession();
      return of(null);
    }

    return this.http.post<User>(AppSettings.AUTH_ME, {}).pipe(
      tap(user => {
        this.user.set(user);
        localStorage.setItem(this.USER_KEY, JSON.stringify(user));
        this.isAuthenticated.set(true);
      }),
      catchError(() => {
        this.clearSession();
        return of(null);
      })
    );
  }

  private setSession(authResponse: AuthResponse): void {
    localStorage.setItem(this.TOKEN_KEY, authResponse.access_token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(authResponse.user));
    this.user.set(authResponse.user);
    this.isAuthenticated.set(true);
  }

  public clearSession(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    this.user.set(null);
    this.isAuthenticated.set(false);
    this.router.navigate(['/login']);
  }
  
  public isAdmin(): boolean {
    return this.user()?.role === 'admin';
  }
}
